import { Asset } from '../../../api';
import { Button, Modal, Upload, Space, Spin } from '@myua/kit';
import { RcFile } from '@myua/kit/dist/antd/upload/interface';
import { useEffect, useState } from 'react';
import { Trash } from '@myua/icons/dist/linear14';
import '../styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import * as previewActions from '../actions';
import { getInProgress, getModal } from '../selectors';
import { ALLOWED_PREVIEW_EXTENSIONS } from '../model';
import { getUser } from '../../app/selectors';

export const PreviewButton: React.FC<{
  asset: Asset;
}> = ({ asset }) => {
  const dispatch = useDispatch();
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [toBeRemoved, setToBeRemoved] = useState(false);
  const inProgress = useSelector(getInProgress);
  const modal = useSelector(getModal);
  const assetId = asset.id;
  const preview = asset.preview;
  const user = useSelector(getUser);

  useEffect(() => {
    setToBeRemoved(false);
    setFileList([]);
  }, [preview, modal]);

  const label = preview ? (preview.is_custom ? 'Custom' : 'Default') : 'None';

  if (!user) {
    return <>{label}</>;
  }

  const close = () => dispatch(previewActions.setModal({ open: false }));

  return (
    <>
      <Button
        className="al-finder-preview-button"
        type="link"
        size="small"
        onClick={() => dispatch(previewActions.setModal({ open: true, assetId }))}
        title="Manage preview"
      >
        {label}
      </Button>
      <Modal
        className="al-finder-preview-modal"
        width={560}
        title="Manage preview"
        open={modal.open}
        onCancel={close}
        footer={
          <Space>
            <Button onClick={close}>Cancel</Button>
            <Button
              color="primary"
              variant="solid"
              onClick={() => {
                if (fileList.length) {
                  const file = fileList[0];
                  dispatch(previewActions.uploadRequested({ assetId, file }));
                } else if (toBeRemoved) {
                  dispatch(previewActions.removeRequested({ assetId }));
                } else {
                  close();
                }
              }}
            >
              Save
            </Button>
          </Space>
        }
      >
        {inProgress[assetId] ? <Spin spinning className="al-finder-preview-modal__spin" /> : null}

        {preview?.is_custom ? (
          <div className="al-finder-preview-modal__block-remove">
            <div>Remove the custom preview to use the default version.</div>
            <div>Generating the default preview may take a few minutes.</div>
            <div className="al-finder-preview-modal__preview-status">
              Preview:{' '}
              {toBeRemoved ? (
                <span>default</span>
              ) : (
                <>
                  <span>custom</span>
                  <Button
                    title="Remove custom"
                    size="small"
                    variant="text"
                    color="orange"
                    icon={<Trash />}
                    onClick={() => setToBeRemoved(true)}
                  />
                </>
              )}
            </div>
          </div>
        ) : null}

        <div className="al-finder-preview-modal__block-upload">
          <div>Select a file to use as a new custom preview.</div>
          <div>Allowed formats: {ALLOWED_PREVIEW_EXTENSIONS.join(', ')}.</div>

          <Upload
            className="al-finder-preview-modal__upload"
            accept={ALLOWED_PREVIEW_EXTENSIONS.map((format) => `.${format}`).join(',')}
            fileList={fileList}
            onRemove={() => {
              setFileList([]);
            }}
            beforeUpload={(file) => {
              setFileList([file]);
              return false;
            }}
            itemRender={(originNode, file, list, { remove }) => (
              <div className="al-finder-preview-modal__upload-item">
                <span className="al-finder-preview-modal__upload-item-name">{file.name}</span>
                <Button
                  title="Remove selection"
                  size="small"
                  type="text"
                  icon={<Trash />}
                  onClick={remove}
                />
              </div>
            )}
          >
            <Button>Select file</Button>
          </Upload>
        </div>
      </Modal>
    </>
  );
};
